import React, { useState, useEffect } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";
import { AppSpinner } from "components/AppSpinner/AppSpinner";

export function InvoicePaymentVerification() {
  const [searchParams] = useSearchParams();
  const { bankName } = useParams<{ bankName?: string }>();
  const [status, setStatus] = useState<
    "loading" | "success" | "error" | "not_found" | "payment_not_found"
  >("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");

  const txId = searchParams.get("tx_id");
  const paymentStatus = searchParams.get("payment_status"); // this happens when redirected back from Azure Function
  const paymentReference = searchParams.get("payment_reference");

  useEffect(() => {
    const verifyPayment = async () => {
      // if paymentStatus exists, it means it was redirected back from Azure Function
      if (paymentStatus) {
        if (paymentStatus==="0") {
          setStatus("success");
          return;
        }
        setStatus("payment_not_found");
        setErrorMessage("Maksājums nav atrasts vai nav samaksāts.");
        return;          
      }

      if (bankName) {
        // Bank payment verification requires tx_id only for SwedbankLV
        if (bankName === "SwedbankLV" && !txId) {
          setStatus("error");
          setErrorMessage("Trūkst nepieciešamo parametru.");
          return;
        }
      } else {
        // Credit card verification requires tx_id and payment_reference
        if (!txId || !paymentReference) {
          setStatus("error");
          setErrorMessage("Trūkst nepieciešamo parametru.");
          return;
        }
      }

      try {
        // Build API URL and determine method based on payment type
        let apiUrl: string;

        if (bankName) {
          // Bank payment
          // Get raw query string to preserve special characters like +
          const rawQueryString = window.location.search.substring(1);
          
          if (bankName === "SwedbankLV") {
            apiUrl = `${process.env.REACT_APP_BASE_PUB_URL}/invoicepayment/verify/bank/${bankName}?tx_id=${txId}`;
          } else {
            // Other banks - pass all query parameters
            apiUrl = `${process.env.REACT_APP_BASE_PUB_URL}/invoicepayment/verify/bank/${bankName}?${rawQueryString}`;            
          }
        } else {
          // Credit card payment
          apiUrl = `${process.env.REACT_APP_BASE_PUB_URL}/invoicepayment/verify/creditcard/${txId}?payment_reference=${paymentReference}`;
        }

        const response = await axios.get(apiUrl)

        console.log("API Response:", response.status, response.data);

        if (response.status === 200) {
          setStatus("success");
        }
      } catch (error: any) {
        console.error("Error verifying payment:", error);

        if (error.response) {
          switch (error.response.status) {
            case 400:
              setStatus("error");
              setErrorMessage("Nepareizi parametri.");
              break;
            case 404:
              setStatus("not_found");
              setErrorMessage("Piedāvājums vai rēķins nav atrasts.");
              break;
            case 409:
              setStatus("payment_not_found");
              setErrorMessage("Maksājums nav atrasts vai nav samaksāts.");
              break;
            case 500:
              setStatus("error");
              setErrorMessage("Radās servera kļūda.");
              break;
            default:
              setStatus("error");
              setErrorMessage("Radās neparedzēta kļūda.");
          }
        } else {
          setStatus("error");
          setErrorMessage("Neizdevās sazināties ar serveri.");
        }
      }
    };

    verifyPayment();
  }, [txId, paymentReference, bankName]);

  const getMessage = () => {
    switch (status) {
      case "success":
        return {
          title: "Maksājums veiksmīgi apstrādāts!",
          description:
            "Jūsu maksājums ir veiksmīgi apstrādāts. Pateicamies par izvēli!",
          icon: "✓",
          color: "#4caf50",
        };
      case "payment_not_found":
        return {
          title: "Maksājums nav atrasts vai nav samaksāts",
          description: errorMessage,
          icon: "⚠",
          color: "#ff9800",
        };
      case "not_found":
        return {
          title: "Piedāvājums vai rēķins nav atrasts",
          description: errorMessage,
          icon: "✕",
          color: "#f44336",
        };
      case "error":
        return {
          title: "Radās kļūda",
          description: errorMessage,
          icon: "✕",
          color: "#f44336",
        };
      default:
        return null;
    }
  };

  const message = getMessage();

  return (
    <Container>
      <Logo src="/greco.svg" alt="GrECo" />
      <Content>
        {status === "loading" ? (
          <LoadingContainer>
            <AppSpinner />
            <LoadingText>Apstrādājam Jūsu maksājumu...</LoadingText>
          </LoadingContainer>
        ) : (
          message && (
            <>
              <StatusIcon $color={message.color}>{message.icon}</StatusIcon>
              <Title>{message.title}</Title>
              <Description>{message.description}</Description>
              <WebsiteLink
                href="https://www.greco.lv/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Apmeklēt mūsu mājas lapu
              </WebsiteLink>
            </>
          )
        )}
      </Content>
    </Container>
  );
}

const Container = styled.div`
  min-height: 100vh;
  width: 100vw;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  background-color: #f5f5f5;
  margin: 0;
  padding: 40px 20px;
  box-sizing: border-box;
  position: relative;
`;

const Logo = styled.img`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 108px;
  height: 108px;
`;

const Content = styled.div`
  background-color: #ffffff;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  padding: 60px 40px;
  max-width: 600px;
  width: 100%;
  margin-top: 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const LoadingText = styled.div`
  font-size: 16px;
  color: #5b6a99;
  font-weight: 500;
`;

const StatusIcon = styled.div<{ $color: string }>`
  font-size: 64px;
  color: ${(props) => props.$color};
  margin-bottom: 20px;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 3px solid ${(props) => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Title = styled.h1`
  margin: 0 0 16px 0;
  font-size: 24px;
  font-weight: 600;
  color: #5b6a99;
`;

const Description = styled.p`
  margin: 0 0 32px 0;
  font-size: 16px;
  line-height: 1.6;
  color: #5b6a99;
`;

const WebsiteLink = styled.a`
  display: inline-block;
  padding: 12px 32px;
  background-color: #5b6a99;
  color: #ffffff;
  text-decoration: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  transition: background-color 0.2s;

  &:hover {
    background-color: #4a5780;
  }
`;
